import {
  prepareAssetRequestBody,
  shouldPrepareAssetEntityRequestBody,
  stringifyPreparedAssetForHttp,
} from './assetPayloadNormalize';
import { ASSET_CREATE_FORM_JSON_FIELD } from '../constants/assetCreateUpload';
import {
  parseGeneralInfoPicklist,
  type GeneralInfoPicklistItem,
} from './generalInfoApi';
import { BASE_URL, BASE_URL_DIRECT, resolveApiPath } from '../baseUrl';
import { obtainFreshAccessViaRefresh } from './authRefresh';
import { fetchWithDevProxyFallback } from './fetchWithDevProxyFallback';
import { resolveApiTokenWithMeta, type ApiTokenSource } from './withApiAuth';
import { notifySessionExpired } from '../auth/sessionExpired';
import { getStoredLoginType, persistAccessTokenOnly } from '../auth/sessionToken';
import { browseFilterAssetTypesFallback } from '../constants/filterableAssetTypes';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordApiLookupIdCandidates } from '../utils/assetRecordPrimaryId';
import { resolveAssetLookupId } from './resolveAssetByShortDisplayId';
import {
  friendlyGatewayFailureSummary,
  friendlyInternalServerErrorSummary,
  httpResponsePrimaryMessage,
} from './httpErrorFormat';

const DEBUG_BODY_PREVIEW_MAX = 900;

/** Filled after each API attempt when `onRequestDebug` is passed (Bookings / troubleshooting). */
export type RequestAssetApiDebugSnapshot = {
  method: string;
  /** Normalized path, e.g. `/bookings` */
  path: string;
  /** Full URL passed to `fetch` (browser: may be same-origin `/dev-api-proxy/...`). */
  url: string;
  /** `BASE_URL` from `baseUrl.ts`. */
  apiBase: string;
  /** HTTPS origin the dev proxy targets (Node scripts use this host directly). */
  upstreamHttpsBase: string;
  nodeEnv: string;
  hasAuthorization: boolean;
  bearerTokenCharCount: number;
  tokenSource: ApiTokenSource;
  contentTypeSent: string | null;
  requestBodyBytes: number | null;
  requestBodyPreview: string | null;
  outcome: 'success' | 'http_error' | 'network_error' | 'client_error';
  httpStatus?: number;
  httpStatusText?: string;
  responseBodyPreview?: string;
  /** Transport error (no HTTP response). */
  transportMessage?: string;
  /** Local validation after HTTP 2xx (e.g. invalid JSON). */
  clientMessage?: string;
};

/** Path pattern for asset lookup — adjust if your API uses a different route. */
export function assetInfoUrl(assetId: string): string {
  const id = encodeURIComponent(assetId.trim());
  return resolveApiPath(`assets/${id}`);
}

/** Rich error for the Assets UI: HTTP status, API code/description, network details. */
export class AssetRequestError extends Error {
  readonly httpStatus?: number;

  readonly httpStatusText?: string;

  readonly errorCode?: string | number;

  readonly errorDescription?: string;

  readonly rawBody?: string;

  readonly networkDetails?: string;

  constructor(opts: {
    message: string;
    httpStatus?: number;
    httpStatusText?: string;
    errorCode?: string | number;
    errorDescription?: string;
    rawBody?: string;
    networkDetails?: string;
  }) {
    super(opts.message);
    this.name = 'AssetRequestError';
    this.httpStatus = opts.httpStatus;
    this.httpStatusText = opts.httpStatusText;
    this.errorCode = opts.errorCode;
    this.errorDescription = opts.errorDescription;
    this.rawBody = opts.rawBody;
    this.networkDetails = opts.networkDetails;
    Object.setPrototypeOf(this, AssetRequestError.prototype);
  }
}

function looksLikeJsonPayload(s: string): boolean {
  const t = s.trim();
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
}

/**
 * One-line, user-facing summary without HTTP status codes. Use the error panel’s technical section for full detail.
 */
export function shortAssetRequestErrorSummary(error: AssetRequestError): string {
  if (error.networkDetails?.trim()) {
    const nd = error.networkDetails.trim();
    if (/failed to fetch/i.test(nd)) {
      return 'Could not reach the server. Check your connection, proxy, or CORS.';
    }
    const head = nd.split(/\n/)[0]?.trim() ?? nd;
    if (head.length <= 200) {
      return head.replace(/\bHTTP\s+\d{3}\b/gi, '').replace(/\s+/g, ' ').trim() || 'Something went wrong.';
    }
    return 'Could not complete the request over the network.';
  }

  const desc = error.errorDescription?.trim();
  const firstLine = desc?.split('\n')[0]?.trim();
  const startsStructured = firstLine ? /^[\[{]/.test(firstLine) : false;
  const usableDescription =
    firstLine &&
    firstLine.length <= 220 &&
    !startsStructured &&
    !looksLikeJsonPayload(firstLine) &&
    !/^HTTP\s*\d{3}/i.test(firstLine);

  if (usableDescription) {
    return firstLine;
  }

  const status = error.httpStatus;
  if (status === 401) {
    return 'Sign in or provide a valid access token.';
  }
  if (status === 403) {
    return 'You do not have permission for this action.';
  }
  if (status === 404) {
    return 'No resource was found for this request.';
  }
  if (status === 409) {
    return 'This could not be completed because of a conflict.';
  }
  if (status != null && status >= 400 && status < 500) {
    return 'The request was not accepted.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return friendlyGatewayFailureSummary(status, error.rawBody ?? error.errorDescription ?? '');
  }
  if (status != null && status >= 500) {
    const detail = (desc ?? error.errorDescription ?? '').replace(/\s+/g, ' ').trim();
    if (
      !detail ||
      /^(internal server error|server error|something went wrong)\.?$/i.test(detail)
    ) {
      return friendlyInternalServerErrorSummary();
    }
    return detail.length <= 220 ? detail : 'The server had a problem. Try again later.';
  }

  const cleaned = error.message
    .replace(/\bHTTP\s+\d{3}\b(?:\s+[^\s—]+)?/gi, '')
    .replace(/^[—\s]+/g, '')
    .replace(/\s*—\s*see .+$/i, '')
    .trim();
  if (cleaned.length > 0 && cleaned.length < 220 && !looksLikeJsonPayload(cleaned)) {
    return cleaned;
  }
  return 'Something went wrong.';
}

const MAX_CAUSE_DEPTH = 5;

/**
 * Formats a thrown value from `fetch` (network, CORS, DNS, etc.) so the UI shows
 * name, message, DOMException numeric code when present, and optional `cause` chain.
 */
export function describeFetchFailure(err: unknown): string {
  const parts: string[] = [];
  let current: unknown = err;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current != null; depth++) {
    if (current instanceof Error) {
      const { name, message } = current;
      const domCode = (current as DOMException).code;
      const hasDomCode =
        typeof domCode === 'number' && !Number.isNaN(domCode) && domCode !== 0;
      const codeSuffix = hasDomCode ? ` [DOMException code: ${domCode}]` : '';
      parts.push(`${name}: ${message}${codeSuffix}`);
      const next = (current as Error & { cause?: unknown }).cause;
      current = next !== undefined ? next : null;
    } else {
      parts.push(String(current));
      break;
    }
  }

  return parts.join(' → cause: ');
}

function firstDefinedCode(...vals: unknown[]): string | number | undefined {
  for (const v of vals) {
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
}

function firstDefinedDescription(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
}

/** Pulls common API error shapes from a JSON object (flat or nested `error`). */
function extractApiErrorFields(obj: Record<string, unknown>): {
  code?: string | number;
  description?: string;
} {
  let code = firstDefinedCode(
    obj.error_code,
    obj.errorCode,
    obj.code,
    obj.errno,
    obj.type,
    obj.error_id,
    obj.errorId,
  );
  let description = firstDefinedDescription(
    obj.description,
    obj.error_description,
    obj.detail,
    obj.title,
    obj.reason,
    obj.message,
    obj.msg,
    obj.errorMessage,
    obj.error_message,
  );

  if (description === undefined && Array.isArray(obj.errors)) {
    const first = obj.errors[0];
    if (first && typeof first === 'object') {
      description = firstDefinedDescription(
        (first as Record<string, unknown>).message,
        (first as Record<string, unknown>).detail,
        (first as Record<string, unknown>).description,
      );
    } else if (typeof first === 'string') {
      description = first.trim();
    }
  }

  if (typeof obj.error === 'string') {
    if (!description) {
      description = obj.error;
    }
  } else if (obj.error && typeof obj.error === 'object' && !Array.isArray(obj.error)) {
    const inner = obj.error as Record<string, unknown>;
    if (code === undefined) {
      code = firstDefinedCode(inner.code, inner.error_code, inner.errorCode, inner.type);
    }
    if (!description) {
      description = firstDefinedDescription(inner.message, inner.description, inner.detail, inner.title);
    }
  }

  return { code, description };
}

function parseErrorResponseBody(rawText: string): {
  code?: string | number;
  description?: string;
} {
  const trimmed = rawText.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return extractApiErrorFields(parsed as Record<string, unknown>);
    }
    if (Array.isArray(parsed)) {
      return { description: JSON.stringify(parsed, null, 2) };
    }
    if (
      parsed === null ||
      typeof parsed === 'string' ||
      typeof parsed === 'number' ||
      typeof parsed === 'boolean'
    ) {
      return { description: String(parsed) };
    }
  } catch {
    /* plain text / HTML body */
  }
  return {};
}

const MAX_ERROR_BODY_CHARS = 96_000;

function truncateForStore(text: string): string {
  if (text.length <= MAX_ERROR_BODY_CHARS) {
    return text;
  }
  return `${text.slice(0, MAX_ERROR_BODY_CHARS)}\n\n… [truncated: ${text.length} characters total]`;
}

/** When the server sends JSON we do not map field-by-field, still show the full payload. */
function looksLikeGatewayHtmlBody(trimmed: string): boolean {
  const head = trimmed.slice(0, 800);
  return (
    /^\s*<(!DOCTYPE\s+)?html\b/i.test(trimmed) ||
    /<\s*title\s*>\s*502\s*Bad\s*Gateway/i.test(head) ||
    (trimmed.includes('<html') && trimmed.includes('</html>'))
  );
}

function fallbackDescriptionFromBody(trimmed: string, parsedFields: { code?: string | number; description?: string }): string | undefined {
  if (parsedFields.description) {
    return parsedFields.description;
  }
  if (!trimmed) {
    return undefined;
  }
  if (looksLikeGatewayHtmlBody(trimmed)) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed !== null && typeof parsed === 'object') {
      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    /* not JSON — use raw server text (plain text, etc.) */
  }
  return trimmed;
}

function userFacingHttpErrorDetail(
  status: number,
  statusText: string,
  description: string | undefined,
  rawBody: string,
): string {
  const bodyForHints = rawBody.trim() || description?.trim() || '';
  if (description?.trim() && !looksLikeGatewayHtmlBody(description)) {
    const line = description.replace(/\s+/g, ' ').trim();
    if (line.length <= 220) {
      return line;
    }
    return `${line.slice(0, 217)}…`;
  }
  if (status === 502 || status === 503 || status === 504) {
    return friendlyGatewayFailureSummary(status, bodyForHints);
  }
  return httpResponsePrimaryMessage(status, bodyForHints);
}

function buildHttpErrorMessage(
  status: number,
  statusText: string,
  code: string | number | undefined,
  description: string | undefined,
  rawBody: string,
): string {
  const parts = [`HTTP ${status}`];
  if (statusText) {
    parts.push(statusText);
  }
  if (code !== undefined) {
    parts.push(`code=${String(code)}`);
  }
  parts.push(userFacingHttpErrorDetail(status, statusText, description, rawBody));
  if (status === 413) {
    parts.push(
      'Request body too large for this API (typical limit ~100 KB JSON or ~500 KB multipart). Update sends only changed fields; shrink images or split edits.',
    );
  }
  return parts.join(' — ');
}

/**
 * Authorised JSON to API routes under the app base URL.
 * `path` is the part after the API base, e.g. `/assets/...` or `/bookings/...`.
 *
 * **Development debugging:** On non-2xx responses, the browser console logs the full JSON request
 * body and full response text (Authorization header value is redacted). Successful **mutations** to
 * routes under `…/assets/…` (PATCH, POST, DELETE) also log a short `console.info` line (method, URL,
 * request body label or preview, response status + preview). For **full** request/response text on
 * **success** as well, set `localStorage.setItem('ofrontend.debugFullAssetApi', '1')` and reload.
 * In DevTools, the **Network** tab always lists every `fetch` (filter by `PATCH` or `assets`).
 *
 */
export async function requestAssetApi(options: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  onRequestDebug?: (snapshot: RequestAssetApiDebugSnapshot) => void;
}): Promise<unknown> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const result = await requestAssetApiOnce(options);
    if (result.kind === 'success') {
      return result.data;
    }
    if (
      result.kind === 'http_error' &&
      result.httpStatus === 401 &&
      attempt === 0 &&
      getStoredLoginType()
    ) {
      const fresh = await obtainFreshAccessViaRefresh();
      if (fresh) {
        persistAccessTokenOnly(fresh);
        continue;
      }
      notifySessionExpired();
    }
    if (result.kind === 'http_error') {
      throw result.error;
    }
    throw result.error;
  }
  throw new Error('requestAssetApi: unreachable');
}

type RequestAssetApiOnceResult =
  | { kind: 'success'; data: unknown }
  | { kind: 'http_error'; httpStatus: number; error: AssetRequestError }
  | { kind: 'network_error'; error: AssetRequestError };

async function requestAssetApiOnce(options: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  onRequestDebug?: (snapshot: RequestAssetApiDebugSnapshot) => void;
}): Promise<RequestAssetApiOnceResult> {
  const emit = options.onRequestDebug;
  const rawPath = options.path.startsWith('/') ? options.path.slice(1) : options.path;
  const p = `/${rawPath}`;
  const prepareEntityBody = shouldPrepareAssetEntityRequestBody(options.method, p);
  let effectiveUrl = resolveApiPath(rawPath);

  /**
   * Match minimal headers on GET (e.g. `all bookings.js` — `Authorization` only) plus `Accept`, same as
   * `checkBaseUrlConnection` / typical browser `fetch`.
   * Omit `Content-Type` for `FormData` so the browser sets `multipart/form-data` with boundary.
   */
  const isFormData =
    typeof FormData !== 'undefined' &&
    options.body !== null &&
    options.body !== undefined &&
    options.body instanceof FormData;

  const headers: Record<string, string> = { Accept: '*/*' };
  if (!isFormData && (options.method === 'POST' || options.method === 'PATCH')) {
    headers['Content-Type'] = 'application/json';
  }
  const { token, source: tokenSource } = await resolveApiTokenWithMeta();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let requestBody: string | undefined;
  if (options.body !== undefined && (options.method === 'POST' || options.method === 'PATCH')) {
    if (isFormData) {
      requestBody = undefined;
    } else {
      const raw = options.body;
      const toSend =
        raw !== null &&
        typeof raw === 'object' &&
        !Array.isArray(raw) &&
        prepareEntityBody
          ? prepareAssetRequestBody(raw)
          : raw;
      requestBody = stringifyPreparedAssetForHttp(toSend);
    }
  }

  const contentTypeFromInit =
    isFormData ? 'multipart/form-data (boundary set by browser)' : headers['Content-Type'] ?? null;

  const baseSnapshot = (): Omit<
    RequestAssetApiDebugSnapshot,
    'outcome' | 'httpStatus' | 'httpStatusText' | 'responseBodyPreview' | 'transportMessage' | 'clientMessage'
  > => ({
    method: options.method,
    path: p,
    url: effectiveUrl,
    apiBase: BASE_URL,
    upstreamHttpsBase: BASE_URL_DIRECT,
    nodeEnv: process.env.NODE_ENV ?? '(unknown)',
    hasAuthorization: Boolean(token),
    bearerTokenCharCount: token ? token.length : 0,
    tokenSource,
    contentTypeSent: contentTypeFromInit,
    requestBodyBytes:
      isFormData || typeof requestBody !== 'string' ? null : requestBody.length,
    requestBodyPreview:
      isFormData && (options.body instanceof FormData)
        ? '(multipart/form-data)'
        : typeof requestBody === 'string' && requestBody.length > 0
          ? `${requestBody.slice(0, DEBUG_BODY_PREVIEW_MAX)}${requestBody.length > DEBUG_BODY_PREVIEW_MAX ? '…' : ''}`
          : null,
  });

  const requestBodyDebugLabel = isFormData
    ? '(multipart/form-data)'
    : requestBody ?? '(no body)';

  let status: number;
  let statusText: string;
  let text: string;
  try {
    const init: RequestInit = {
      method: options.method,
      headers,
      cache: 'no-store',
    };
    if (isFormData && options.body instanceof FormData) {
      init.body = options.body;
    } else if (
      typeof requestBody === 'string' &&
      (options.method === 'POST' || options.method === 'PATCH')
    ) {
      init.body = requestBody;
    }
    const { response, urlUsed } = await fetchWithDevProxyFallback(rawPath, init);
    effectiveUrl = urlUsed;
    status = response.status;
    statusText = response.statusText;
    text = await response.text();
  } catch (err) {
    const networkDetails = describeFetchFailure(err);
    if (process.env.NODE_ENV === 'development') {
      console.warn('[requestAssetApi] Full request (network / transport error — no HTTP response)', {
        method: options.method,
        path: p,
        url: effectiveUrl,
        requestHeaders: {
          Accept: headers.Accept,
          'Content-Type': headers['Content-Type'] ?? null,
          Authorization: token ? `Bearer <${token.length} chars redacted>` : null,
        },
        requestBody: requestBodyDebugLabel,
        transportError: networkDetails,
      });
    }
    emit?.({
      ...baseSnapshot(),
      outcome: 'network_error',
      transportMessage: networkDetails,
    });
    return {
      kind: 'network_error',
      error: new AssetRequestError({
        message: networkDetails,
        networkDetails,
      }),
    };
  }

  const responsePreview =
    text.length > 0
      ? `${text.slice(0, DEBUG_BODY_PREVIEW_MAX)}${text.length > DEBUG_BODY_PREVIEW_MAX ? '…' : ''}`
      : undefined;

  const responseTrimmed = text.trim();
  const rawBody = responseTrimmed ? truncateForStore(responseTrimmed) : undefined;
  const ok = status >= 200 && status < 300;
  if (!ok) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[requestAssetApi] Full request / full backend response (HTTP error — copy for backend team)',
        {
          method: options.method,
          path: p,
          url: effectiveUrl,
          requestHeaders: {
            Accept: headers.Accept,
            'Content-Type': headers['Content-Type'] ?? null,
            Authorization: token ? `Bearer <${token.length} chars redacted>` : null,
          },
          requestBody: requestBodyDebugLabel,
          response: {
            httpStatus: status,
            httpStatusText: statusText,
            body: text,
          },
        },
      );
    }
    emit?.({
      ...baseSnapshot(),
      outcome: 'http_error',
      httpStatus: status,
      httpStatusText: statusText || undefined,
      responseBodyPreview: responsePreview,
    });
    const parsed = parseErrorResponseBody(text);
    const errorDescription = fallbackDescriptionFromBody(responseTrimmed, parsed);
    const message = buildHttpErrorMessage(
      status,
      statusText,
      parsed.code,
      parsed.description ?? errorDescription,
      responseTrimmed,
    );
    return {
      kind: 'http_error',
      httpStatus: status,
      error: new AssetRequestError({
        message: rawBody ? `${message} — see “Server response” below.` : message,
        httpStatus: status,
        httpStatusText: statusText || undefined,
        errorCode: parsed.code,
        errorDescription,
        rawBody,
      }),
    };
  }

  if (process.env.NODE_ENV === 'development') {
    try {
      const fullDebug =
        typeof window !== 'undefined' && window.localStorage?.getItem('ofrontend.debugFullAssetApi') === '1';
      if (fullDebug) {
        console.info(
          '[requestAssetApi] Full request / response (set localStorage ofrontend.debugFullAssetApi = 1)',
          {
            method: options.method,
            path: p,
            url: effectiveUrl,
            requestHeaders: {
              Accept: headers.Accept,
              'Content-Type': headers['Content-Type'] ?? null,
              Authorization: token ? `Bearer <${token.length} chars redacted>` : null,
            },
            requestBody: requestBodyDebugLabel,
            response: {
              httpStatus: status,
              httpStatusText: statusText,
              body: text,
            },
          },
        );
      } else if (options.method !== 'GET' && /\/assets|\/search\/assets/i.test(p)) {
        const snap = baseSnapshot();
        console.info(`[requestAssetApi] ${options.method} ${p}`, effectiveUrl, {
          requestBody: snap.requestBodyPreview ?? requestBodyDebugLabel,
          response: { httpStatus: status, httpStatusText: statusText, bodyPreview: responsePreview },
        });
      }
    } catch {
      /* ignore localStorage */
    }
  }

  if (responseTrimmed.length === 0) {
    emit?.({
      ...baseSnapshot(),
      outcome: 'success',
      httpStatus: status,
      httpStatusText: statusText || undefined,
      responseBodyPreview: '(empty body)',
    });
    return { kind: 'success', data: null };
  }

  try {
    const parsed = JSON.parse(text) as unknown;
    emit?.({
      ...baseSnapshot(),
      outcome: 'success',
      httpStatus: status,
      httpStatusText: statusText || undefined,
      responseBodyPreview: responsePreview,
    });
    return { kind: 'success', data: parsed };
  } catch {
    /** Some routes return 2xx with plain text (e.g. `"Booking created successfully"`) instead of JSON. */
    emit?.({
      ...baseSnapshot(),
      outcome: 'success',
      httpStatus: status,
      httpStatusText: statusText || undefined,
      responseBodyPreview: responsePreview,
    });
    return { kind: 'success', data: { message: responseTrimmed } };
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Outer keys that often wrap the real entity; if **every** root key is allowed here, `data` may be the asset.
 * Add names only when safe (do not add generic words like `items` or `errors` that often mean “list of problems”).
 */
const ASSET_PAYLOAD_ENVELOPE_KEYS = new Set([
  'data',
  'success',
  'status',
  'message',
  'code',
  'error',
  'messagetype',
  'messagedetail',
  'messagetypedetail',
  'resultcode',
  'timestamp',
  'pagination',
  'meta',
  'links',
]);

function unwrapAssetEnvelopeOnce(o: Record<string, unknown>): unknown {
  const lowerKeys = Object.keys(o).map((k) => k.toLowerCase());
  const looksLikeEnvelopeAroundData =
    lowerKeys.length > 0 &&
    lowerKeys.includes('data') &&
    isPlainObject(o.data) &&
    lowerKeys.every((k) => ASSET_PAYLOAD_ENVELOPE_KEYS.has(k));
  if (looksLikeEnvelopeAroundData) {
    return o.data as unknown;
  }

  if (lowerKeys.length === 1 && lowerKeys[0] === 'data' && isPlainObject(o.data)) {
    return o.data as unknown;
  }

  if (
    lowerKeys.length === 1 &&
    lowerKeys[0] === 'asset' &&
    isPlainObject(o.asset) &&
    typeof o.asset !== 'function'
  ) {
    return o.asset as unknown;
  }

  if (
    lowerKeys.length === 1 &&
    lowerKeys[0] === 'result' &&
    isPlainObject(o.result) &&
    typeof o.result !== 'function'
  ) {
    return o.result as unknown;
  }

  return o;
}

/** Unwrap one or more `{ data | asset | result: entity }` layers from GET/create responses for UI + id extraction. */
export function unwrapAssetEnvelope(raw: unknown): unknown {
  let cur: unknown = raw;
  for (let depth = 0; depth < 6; depth++) {
    if (!isPlainObject(cur)) {
      break;
    }
    const next = unwrapAssetEnvelopeOnce(cur);
    if (next === cur) {
      break;
    }
    cur = next;
  }
  return cur;
}

function coerceScalarAssetId(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/**
 * Interpret create (or lightweight success) payloads so we can fill the Display Asset ID field.
 * Covers flat ids, PascalCase (.NET), and string or object `data`.
 */
export function extractAssetIdentifierFromApiPayload(raw: unknown): string | null {
  const scalar = coerceScalarAssetId(raw);
  if (scalar !== null) {
    return scalar;
  }
  if (!isPlainObject(raw)) {
    return null;
  }

  const literalKeys = [
    'id',
    'Id',
    'UUID',
    'uuid',
    'asset_id',
    'assetId',
    'AssetId',
    'facility_id',
    'facilityId',
    'FacilityId',
  ];

  for (const k of literalKeys) {
    if (Object.prototype.hasOwnProperty.call(raw, k)) {
      const c = coerceScalarAssetId(raw[k]);
      if (c !== null) {
        return c;
      }
    }
  }

  const dataBare = coerceScalarAssetId(raw.data);
  if (dataBare !== null) {
    return dataBare;
  }

  const inner = unwrapAssetEnvelope(raw);
  if (inner !== raw) {
    return extractAssetIdentifierFromApiPayload(inner);
  }

  return null;
}

/**
 * Detects hyphenated GUID-like IDs with wrong segment lengths (common copy/paste truncation).
 * RFC 4122-style hyphenated UUID: 8-4-4-4-12 hexadecimal digits per segment.
 * Returns null when the value is not five hex-only hyphenated parts or already matches lengths.
 */
export function explainHyphenatedMalformedUuid(assetIdCandidate: string): string | null {
  const trimmed = assetIdCandidate.trim();
  const parts = trimmed.split('-');
  if (parts.length !== 5) {
    return null;
  }
  const expectedLengths: readonly number[] = [8, 4, 4, 4, 12];
  for (let i = 0; i < 5; i++) {
    if (!/^[0-9a-f]+$/i.test(parts[i])) {
      return null;
    }
  }
  const actual = parts.map((p) => p.length);
  if (actual.every((len, i) => len === expectedLengths[i])) {
    return null;
  }
  const lastLen = parts[4].length;
  const lastHint =
    lastLen < 12
      ? ` The last segment has ${lastLen} hex characters but UUIDs require 12 (you may have dropped a trailing digit).`
      : lastLen > 12
        ? ` The last segment has ${lastLen} hex characters but UUIDs require 12.`
        : '';
  return (
    `Malformed UUID segment lengths (${actual.join('-')}; expected 8-4-4-4-12).${lastHint}`
  );
}

/** POST `/assets/create` with JSON body, same auth headers as other asset API calls. */
export async function createAsset(body: unknown): Promise<unknown> {
  return requestAssetApi({ method: 'POST', path: '/assets/create', body });
}

/**
 * Build `multipart/form-data` for `POST /assets/create` when the service uses `FilesInterceptor('images', 20)`.
 * Stringified DTO goes in part {@link ASSET_CREATE_FORM_JSON_FIELD} (`payload` by default — set `REACT_APP_ASSET_CREATE_FORM_JSON_FIELD` if your API uses another name).
 */
export function buildAssetCreateMultipartFormData(args: {
  preparedPayload: Record<string, unknown>;
  imageFiles: File[];
}): FormData {
  const fd = new FormData();
  fd.append(ASSET_CREATE_FORM_JSON_FIELD, JSON.stringify(args.preparedPayload));
  for (const file of args.imageFiles) {
    fd.append('images', file, file.name);
  }
  return fd;
}

/** POST `/assets/create` with `FormData` (file parts + JSON part); used when local image files are uploaded. */
export async function createAssetWithMultipartFormData(formData: FormData): Promise<unknown> {
  return requestAssetApi({ method: 'POST', path: '/assets/create', body: formData });
}

/**
 * PATCH `/assets/:id` with `FormData`; same field names as {@link createAssetWithMultipartFormData}.
 *
 * **Why create can work but update multipart fails:** `POST /assets/create` and this PATCH both send a JSON
 * `payload` plus `images` file parts, but PATCH often repeats the **entire** asset in JSON (long existing
 * image URLs, opening times, …) so the request is **much larger**. Proxies, load balancers, or the API may
 * drop or time out on large **PATCH** bodies even when **POST** succeeds. If that happens, try
 * **`REACT_APP_ASSET_PATCH_MULTIPART_JSON_MINIMAL=true`** in `.env.development.local` (see **`src/constants/assetCreateUpload.ts`**) only
 * if your backend merges partial PATCH data.
 */
export async function updateAssetWithMultipartFormData(
  assetId: string,
  formData: FormData,
): Promise<unknown> {
  const trimmed = assetId.trim();
  if (!trimmed) {
    throw new Error('Asset ID is required.');
  }
  return requestAssetApi({
    method: 'PATCH',
    path: `/assets/${encodeURIComponent(trimmed)}`,
    body: formData,
  });
}

export async function getAssetById(assetId: string): Promise<unknown> {
  const trimmed = assetId.trim();
  if (!trimmed) {
    throw new Error('Asset ID is required.');
  }
  const lookupId = await resolveAssetLookupId(trimmed);
  const uuidShapeHint = explainHyphenatedMalformedUuid(lookupId);
  if (uuidShapeHint) {
    throw new Error(
      `${uuidShapeHint} Paste the full id from Create or List, or inspect Raw JSON.`,
    );
  }
  const raw = await requestAssetApi({
    method: 'GET',
    path: `/assets/${encodeURIComponent(lookupId)}`,
  });
  return unwrapAssetEnvelope(raw);
}

/** GET `/assets/:id` trying mutation id, numeric id, then UUID aliases from a list/detail row. */
export async function getAssetByIdBestEffort(
  row: Record<string, unknown>,
  ...hintIds: string[]
): Promise<unknown> {
  const candidates = assetRecordApiLookupIdCandidates(coalesceAssetListRecord(row), ...hintIds);
  if (candidates.length === 0) {
    throw new Error('Asset ID is required.');
  }
  let lastError: unknown;
  for (const candidate of candidates) {
    try {
      return await getAssetById(candidate);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Asset not found.');
}

let assetTypesBrowseCache: GeneralInfoPicklistItem[] | null = null;

/** GET `/assets/asset-types` — catalog for Create Asset asset type dropdown. */
export async function fetchAssetTypes(): Promise<GeneralInfoPicklistItem[]> {
  const raw = await requestAssetApi({ method: 'GET', path: '/assets/asset-types' });
  const parsed = parseGeneralInfoPicklist(raw);
  if (parsed.length > 0) {
    assetTypesBrowseCache = parsed;
  }
  return parsed;
}

/**
 * Browse/home asset-type catalog — tolerates `GET /assets/asset-types` gateway failures by reusing
 * the last good response or the eleven-slot browse fallback list.
 */
export async function fetchAssetTypesForBrowse(): Promise<GeneralInfoPicklistItem[]> {
  try {
    return await fetchAssetTypes();
  } catch {
    if (assetTypesBrowseCache && assetTypesBrowseCache.length > 0) {
      return assetTypesBrowseCache;
    }
    return browseFilterAssetTypesFallback();
  }
}

/** GET `/assets/facilities` — catalog for Create Asset facility checkboxes. */
export async function fetchAssetFacilities(): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/assets/facilities' });
}

/** GET `/assets/addons` — catalog for Create Asset addon selection. */
export async function fetchAssetAddons(): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/assets/addons' });
}

/** GET `/assets/pricing-types` — catalog for Create Asset pricing checkboxes. */
export async function fetchAssetPricingTypes(): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/assets/pricing-types' });
}
